import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { levelOf } from '@/lib/perm';
import { useLang } from '@/lib/i18n';
import { AppShell } from '@/components/AppShell';
import { ModuleIcon } from '@/components/ModuleIcon';
import { api } from '@/lib/api';
import { GlobalSearch } from '@/components/GlobalSearch';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export default function AppsPage() {
  const { company, user } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const modules = (company?.modules || []).filter((m) => m.available && m.enabled && levelOf(company, m.key) !== 'no');
  const [webmailUnread, setWebmailUnread] = useState(0);
  useEffect(() => {
    if (!company || !modules.find((m) => m.key === 'webmail')) return undefined;
    const tick = () => api.get(`/companies/${company.id}/webmail/unread-count`).then(({ data }) => setWebmailUnread(data.unread || 0)).catch(() => {});
    tick(); const h = setInterval(tick, 30000); return () => clearInterval(h);
  }, [company?.id]); // eslint-disable-line
  const sub = company?.subscription;

  const tiles = [
    ...modules,
    ...(user?.is_super_admin ? [{ key: 'super_admin', label_id: 'Super Admin', label_en: 'Super Admin', available: true, enabled: true, path: '/admin', desc_id: 'Panel operator platform', desc_en: 'Platform operator console' }] : []),
  ];

  return (
    <AppShell className="bg-apps-wash">
      <div className="mx-auto max-w-5xl px-4 py-10 md:py-16">
        <div className="relative z-20 text-center mb-10 fade-up">
          <GlobalSearch company={company} tiles={tiles} lang={lang} />
          {sub && !sub.is_valid && (
            <button type="button" onClick={() => navigate('/settings/billing')} className="mt-4 inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-4 py-1.5 text-sm text-rose-800" data-testid="apps-subscription-warning">
              {t('subscription_inactive')} — {t('pay_now')}
            </button>
          )}
        </div>
        <TooltipProvider delayDuration={200}>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-6 sm:gap-8 justify-items-center" data-testid="apps-grid">
            {tiles.map((m, i) => {
              const clickable = m.available && (m.enabled || m.key === 'settings');
              const label = lang === 'id' ? m.label_id : m.label_en;
              return (
                <Tooltip key={m.key}>
                  <TooltipTrigger asChild>
                    <motion.button
                      type="button"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03, duration: 0.22 }}
                      whileHover={clickable ? { y: -3 } : {}}
                      whileTap={clickable ? { scale: 0.97 } : {}}
                      onClick={() => (clickable ? navigate(m.path) : navigate('/modules'))}
                      data-testid={`apps-grid-tile-${m.key}`}
                      className={`group w-[88px] sm:w-[104px] flex flex-col items-center focus:outline-none ${clickable ? '' : 'opacity-70'}`}
                    >
                      <div className={`relative flex h-[72px] w-[72px] sm:h-[88px] sm:w-[88px] items-center justify-center rounded-[22px] bg-white border border-border shadow-tile transition-shadow ${clickable ? 'group-hover:shadow-lg' : ''} group-focus-visible:ring-2 group-focus-visible:ring-ring`}>
                        {m.key === 'super_admin' ? (
                          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[rgba(27,42,107,0.10)]"><ShieldCheck size={24} className="text-primary" /></div>
                        ) : (
                          <ModuleIcon moduleKey={m.key} />
                        )}
                        {m.key === 'webmail' && webmailUnread > 0 && <span className="absolute -right-1.5 -top-1.5 flex h-6 min-w-[24px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white shadow" data-testid="apps-webmail-badge">{webmailUnread > 99 ? '99+' : webmailUnread}</span>}
                      </div>
                      <span className="mt-2.5 text-xs sm:text-sm font-medium text-foreground text-center leading-tight">{label}</span>
                      {!m.available && <Badge variant="secondary" className="mt-1 rounded-full text-[10px] px-2 py-0 font-medium">{t('coming_soon')}</Badge>}
                      {m.available && !m.enabled && m.key !== 'settings' && <Badge variant="outline" className="mt-1 rounded-full text-[10px] px-2 py-0 font-medium">{t('enable')}</Badge>}
                    </motion.button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[220px] text-center">{lang === 'id' ? m.desc_id : m.desc_en}</TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </TooltipProvider>
      </div>
    </AppShell>
  );
}
